import { useState, useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import { useAuth } from "@/hooks/domains/auth/useAuth";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

export const useAuthButton = () => {
  const { user, isLoading: loading, signOut, session } = useAuth();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const { mutate: signOutMutation } = signOut;
  const [isAdmin, setIsAdmin] = useState(false);
  const [isCheckingAdmin, setIsCheckingAdmin] = useState(false);
  const adminCheckComplete = useRef(false);
  const router = useRouter();

  useEffect(() => {
    let isMounted = true;

    const checkAdminStatus = async () => {
      if (user && !isCheckingAdmin && !adminCheckComplete.current) {
        try {
          setIsCheckingAdmin(true);

          const { data, error } = await supabase
            .from("profiles")
            .select("is_admin")
            .eq("id", user.id)
            .single();

          if (error) {
            console.error("Error checking admin status:", error);
            if (isMounted) {
              setIsCheckingAdmin(false);
            }
            return;
          }

          if (data && isMounted) {
            setIsAdmin(!!data.is_admin);
            adminCheckComplete.current = true;
          } else if (isMounted) {
            setIsAdmin(false);
            adminCheckComplete.current = true;
          }
        } catch (err) {
          console.error("Exception checking admin status:", err);
          if (isMounted) {
            setIsAdmin(false);
            adminCheckComplete.current = true;
          }
        } finally {
          if (isMounted) {
            setIsCheckingAdmin(false);
          }
        }
      } else if (!user && isMounted) {
        setIsAdmin(false);
        adminCheckComplete.current = false;
      }
    };

    if (!loading) {
      checkAdminStatus();
    }

    return () => {
      isMounted = false;
    };
  }, [user, loading, isCheckingAdmin]);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await signOutMutation();
      // Reset admin check state
      adminCheckComplete.current = false;
      setIsAdmin(false);
      // Navigate to homepage after successful logout
      router.push("/");
    } catch (error) {
      console.error("Error during logout:", error);
      // Still reset states even if there was an error
      adminCheckComplete.current = false;
      setIsAdmin(false);
    } finally {
      setIsLoggingOut(false);
    }
  };

  const handleRetry = () => {
    window.location.reload();
  };

  return {
    user,
    loading,
    isLoggingOut,
    handleLogout,
    handleRetry,
    isAdmin,
    session,
  };
};
